/**
 * "Remember Me" for /login — a purely CLIENT-side convenience. When the visitor
 * opts in and login succeeds, the typed identifier (email OR username) is stored
 * in localStorage and pre-filled on the next visit. It NEVER stores the password,
 * touches no cookie, no server, and no session/maxAge — config.ts and all session
 * logic are untouched. All access is guarded (SSR-safe + try/catch) so a
 * disabled/blocked localStorage never breaks login.
 *
 * Mirrors ikroot-admin's src/lib/auth/remembered-identifier.ts (FA-AUTH-08) exactly
 * — same shape, only the namespaced key differs per app.
 */
export const REMEMBERED_IDENTIFIER_KEY = "startup-os:remembered-identifier";

/** The remembered identifier, or "" when none / storage is unavailable. */
export function getRememberedIdentifier(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(REMEMBERED_IDENTIFIER_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Persist the identifier (email or username — never the password). */
export function rememberIdentifier(identifier: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(REMEMBERED_IDENTIFIER_KEY, identifier);
  } catch {
    /* storage unavailable — remembering is best-effort, never fatal */
  }
}

/** Forget any previously remembered identifier. */
export function clearRememberedIdentifier(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REMEMBERED_IDENTIFIER_KEY);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}
